// probeTriggers.js — cues fired by a moving object instead of the transport
// playhead. A target opts in by naming its source: trig:<uid>. The source is
// any element carrying that uid (a trans/o2p mover, a drag() object, a
// controlXY point …) — nothing is declared on it. Every frame each referenced
// source's screen rect is tested against its targets' rects; a target fires
// when the source enters it and re-arms when it leaves, so a looping mover
// retriggers on every pass (once:1 fires a single time). Notation hosts
// expand to their notes, so a reading line plays a stave note by note.
// Screen space (getBoundingClientRect) makes nested transforms, page
// overlays and the scrolling score all the same case. Slow moves are swept
// (the centre's path between frames is tested too) so a thin target cannot
// slip between two frames; fast jumps are not, so a leap across the page
// fires nothing on the way. Slow and fast are measured in the source's SVG
// units, so the zoom does not change them.

import { findElementByUid } from "./animShared.js";
import { noteRef, setUidResolver } from "../control/refs.js";
import { onReset } from "../system/teardown.js";
// Re-exported for the modules that import it from here; the list itself
// lives in a leaf module so it can be tested without a browser.
export { isObjectTrig, KNOWN_TRIGS } from "./trigModes.js";

// refs.js stays a leaf — it cannot import animShared.js without dragging the
// whole overlay stack behind it. This is the cue layer handing it the lookup.
setUidResolver(findElementByUid);

const _targets = new Map();   // sourceUid → Set<{ el, fire, once, fired, inside, notes }>
let _raf = 0;

export function registerProbeTarget(sourceUid, el, fire, { once = false } = {}) {
  // Checked after the walk, not now: the source may be registered later in the
  // same pass. Unchecked, a trig:typo spins the rAF loop forever and never
  // fires — with the cue also off the playhead list, so dead and silent.
  noteRef({ kind: "trig", name: sourceUid, el, cueExpr: el?.id, resolve: () => sourceElement(sourceUid) });

  let set = _targets.get(sourceUid);
  if (!set) { set = new Set(); _targets.set(sourceUid, set); }
  for (const t of set) {
    if (t.el === el) { t.fire = fire; t.once = once; return t; }   // rescan: refresh, keep state
  }
  const t = { el, fire, once, fired: false, inside: false, notes: null };
  set.add(t);
  _ensureLoop();
  return t;
}

export function unregisterProbeTargetsFor(el) {
  for (const set of _targets.values()) {
    for (const t of set) if (t.el === el) set.delete(t);
  }
}

/** Drop every target inside root — a page being replaced or torn down. */
export function unregisterProbeTargetsWithin(root) {
  if (!root) return;
  for (const set of _targets.values()) {
    for (const t of set) if (root.contains(t.el)) set.delete(t);
  }
}

/** Forget every target and stop the loop: the project is being left. */
export function resetProbeTriggers() {
  _targets.clear();
  _prev.clear();
  if (_raf) { cancelAnimationFrame(_raf); _raf = 0; }
}
onReset("project", resetProbeTriggers);

export function isProbeSource(uid) {
  const set = _targets.get(uid);
  return !!set && set.size > 0;
}

function sourceElement(uid) {
  return window.oscillaAnimRegistry?.[uid]?.el || findElementByUid(uid)?.el || null;
}

// Liang–Barsky: does the segment (x0,y0)→(x1,y1) touch rect r?
function segmentHitsRect(x0, y0, x1, y1, r) {
  let t0 = 0, t1 = 1;
  const dx = x1 - x0, dy = y1 - y0;
  const clip = (p, q) => {
    if (p === 0) return q >= 0;
    const t = q / p;
    if (p < 0) { if (t > t1) return false; if (t > t0) t0 = t; }
    else       { if (t < t0) return false; if (t < t1) t1 = t; }
    return true;
  };
  return clip(-dx, x0 - r.left) && clip(dx, r.right - x0) &&
         clip(-dy, y0 - r.top)  && clip(dy, r.bottom - y0);
}

function fire(state, once, el, fn, uid) {
  if (once && state.fired) return;
  state.fired = true;
  try { fn(); } catch (err) { console.warn("[probe] target failed:", err); }
  window.dispatchEvent(new CustomEvent("oscilla:probe-hit", { detail: { source: uid, target: el } }));
}

// Faster than this is a jump, not a sweep: a reading line leaping to the
// next stave must not play the notes it flies over, while a slow pass must
// not skip a thin note between two frames. The limit is 2.5 screen px per ms
// (≈ 2500 px/s) at fit zoom, turned into the source's SVG units (world units
// for the score) at the fit scale, so zooming in or out does not change which
// moves are jumps.
const MAX_SWEEP_PX_PER_MS_AT_FIT = 2.5;
const _prev = new Map();   // sourceUid → { el, cx, cy, t }

// Screen px per SVG unit of the source's SVG, now and at fit zoom. Only the
// score is zoomed (window.scoreZoom: scoreZoom.js is the score app's, which
// the engine does not import); a page's SVG is at fit whatever the zoom. An
// HTML source has no units but screen px.
function sourceScale(el) {
  const svg = el instanceof SVGElement ? (el.ownerSVGElement || el) : null;
  const m = svg?.getScreenCTM?.();
  const now = m ? Math.hypot(m.a, m.b) : 0;
  if (!(now > 0)) return { now: 1, fit: 1 };
  const zoom = window.scoreContainer?.contains(svg) && Number(window.scoreZoom) > 0 ? Number(window.scoreZoom) : 1;
  return { now, fit: now / zoom };
}

// One target (or one notation note) against the source. `st` holds the
// target's inside/fired state; `src` the source's rect, centre and, when the
// move since last frame was slow enough, its previous centre.
function test(st, once, el, rect, src, fn, uid) {
  const half = { w: src.rect.width / 2, h: src.rect.height / 2 };
  // target grown by the source's half-size: the source's centre being inside
  // it is exactly box-against-box overlap
  const ex = { left: rect.left - half.w, right: rect.right + half.w, top: rect.top - half.h, bottom: rect.bottom + half.h };
  const now = src.cx > ex.left && src.cx < ex.right && src.cy > ex.top && src.cy < ex.bottom;
  const hit = now || (src.prev ? segmentHitsRect(src.prev.cx, src.prev.cy, src.cx, src.cy, ex) : false);
  if (hit && !st.inside) fire(st, once, el, fn, uid);
  // "inside" means hit this frame, not merely overlapping now: a sweep that
  // ends just short of the target followed by a frame inside it is one
  // entry, not two. Once neither overlap nor sweep touches it, it re-arms.
  st.inside = hit;
}

function tick() {
  _raf = 0;
  // Same suppression as playhead cues: loading, scrubbing, sync jumps.
  if (!window.suppressCueTriggers && !window._isTeleporting) {
    const now = performance.now();
    for (const [uid, set] of _targets) {
      // targets gone from the page go first, so a source gone with them
      // leaves nothing to look up and the loop can stop
      for (const t of set) if (!t.el.isConnected) set.delete(t);
      if (!set.size) { _targets.delete(uid); _prev.delete(uid); continue; }
      const el = sourceElement(uid);
      if (!el?.isConnected) { _prev.delete(uid); continue; }
      const rect = el.getBoundingClientRect();
      if (!rect.width && !rect.height) continue;
      const cx = rect.left + rect.width / 2, cy = rect.top + rect.height / 2;
      const p = _prev.get(uid);
      let prev = null;
      if (p && p.el === el) {
        const dt = Math.max(1, now - p.t);
        const scale = sourceScale(el);
        // SVG units per ms, against the limit in the same units
        const speed = Math.hypot(cx - p.cx, cy - p.cy) / scale.now / dt;
        if (speed <= MAX_SWEEP_PX_PER_MS_AT_FIT / scale.fit) prev = p;
      }
      _prev.set(uid, { el, cx, cy, t: now });
      const src = { rect, cx, cy, prev };
      for (const t of set) {
        const g = t.el._notationGroup;
        if (g) {
          // A notation host: enter/leave per note, firing that note's OSC.
          if (!t.notes) t.notes = new WeakMap();
          for (const noteEl of g.querySelectorAll(".notation-note")) {
            if (!noteEl._notationOscFire) continue;
            let st = t.notes.get(noteEl);
            if (!st) { st = { inside: false, fired: false }; t.notes.set(noteEl, st); }
            test(st, t.once, noteEl, noteEl.getBoundingClientRect(), src, () => {
              noteEl._notationOscFire(noteEl._notationOscPayload?.index, "probe");
              for (const f of noteEl._notationGlissFires || []) { try { f(); } catch { /* one gliss failing must not stop the note */ } }
            }, uid);
          }
          continue;
        }
        test(t, t.once, t.el, t.el.getBoundingClientRect(), src, t.fire, uid);
      }
    }
  }
  if (_targets.size) _raf = requestAnimationFrame(tick);
}

function _ensureLoop() {
  if (!_raf) _raf = requestAnimationFrame(tick);
}

// While the page is hidden rAF is silent; the worker clock (backgroundClock)
// ticks us instead so a mover keeps firing what it crosses.
window.oscillaBackgroundClock?.onBackgroundTick(() => { if (_targets.size) probeTickNow(); });
window.addEventListener("oscilla:background-clock-ready", () => {
  window.oscillaBackgroundClock?.onBackgroundTick(() => { if (_targets.size) probeTickNow(); });
}, { once: true });

/** Run one hit-test pass now (tests / hidden tabs, where rAF is idle). */
export function probeTickNow() {
  if (_raf) { cancelAnimationFrame(_raf); _raf = 0; }
  tick();
}
window.probeTickNow = probeTickNow;

window.oscillaProbeTargets = _targets;
